import type { ReactNode } from "react";
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, KeyRound } from "lucide-react";

import {
  AuthAlert,
  AuthHeading,
  AuthPasswordField,
  AuthPrimaryButton,
  PasswordChecklist,
  PasswordStrength,
} from "./auth-primitives";
import { authFormWidthClass } from "./auth-tokens";
import { DEFAULT_RESET_PASSWORD_COPY, type ResetPasswordFormCopy } from "./auth-copy";

export interface ResetPasswordFormProps {
  onSubmit: (password: string) => Promise<void>;
  /** Omit to hide the "Back to Sign In" link (e.g. the invite flow, where there is no account to go back to). */
  onNavigateToSignin?: () => void;
  logoSrc?: string;
  /** Copy overrides so the invite flow can reuse this form's password-pair and validation. */
  title?: string;
  description?: string;
  passwordLabel?: string;
  confirmPasswordLabel?: string;
  submitLabel?: string;
  submittingLabel?: string;
  /** Rendered above the password fields — used by the invite flow for the display-name input. */
  children?: ReactNode;
  /**
   * Overrides for the strings not already covered by the scalar props above —
   * the validation messages and the back-link. See `auth-copy.ts`.
   */
  copy?: Partial<ResetPasswordFormCopy>;
}

export const ResetPasswordForm = ({
  onSubmit,
  onNavigateToSignin,
  logoSrc,
  title = "Create a new password",
  description = "Choose something you will remember but nobody would guess.",
  passwordLabel = "New password",
  confirmPasswordLabel = "Confirm new password",
  submitLabel = "Reset password",
  submittingLabel = "Updating password…",
  children,
  copy,
}: ResetPasswordFormProps) => {
  const c = { ...DEFAULT_RESET_PASSWORD_COPY, ...copy };
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError(c.tooShort);
      return;
    }
    if (password !== confirmPassword) {
      setError(c.passwordsMismatch);
      return;
    }

    setLoading(true);
    try {
      await onSubmit(password);
    } catch (err) {
      setError(err instanceof Error ? err.message : c.failed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={authFormWidthClass}>
      <AuthHeading
        icon={
          logoSrc ? (
            <img src={logoSrc} alt="" className="h-9 w-9 object-contain" />
          ) : (
            <KeyRound className="h-6 w-6 text-primary" />
          )
        }
        title={title}
        description={description}
      />

      <motion.form
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
        onSubmit={handleResetPassword}
        className="mt-7 space-y-4"
      >
        {children}

        <div className="space-y-2">
          <AuthPasswordField
            name="password"
            label={passwordLabel}
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
            showPasswordLabel={c.showPasswordLabel}
            hidePasswordLabel={c.hidePasswordLabel}
          />
          <PasswordStrength password={password} labels={c.passwordStrengthLabels} />
          <PasswordChecklist password={password} labels={c.passwordRuleLabels} />
        </div>

        <AuthPasswordField
          name="confirmPassword"
          label={confirmPasswordLabel}
          value={confirmPassword}
          onChange={setConfirmPassword}
          autoComplete="new-password"
          required
          hint={c.matchHint}
          showPasswordLabel={c.showPasswordLabel}
          hidePasswordLabel={c.hidePasswordLabel}
        />

        <AuthAlert message={error} />

        <div className="pt-1">
          <AuthPrimaryButton loading={loading} loadingLabel={submittingLabel}>
            {submitLabel}
            <ArrowRight className="h-4 w-4 opacity-70 transition-transform duration-200 group-hover:translate-x-0.5" />
          </AuthPrimaryButton>
        </div>
      </motion.form>

      {onNavigateToSignin ? (
        <button
          type="button"
          onClick={onNavigateToSignin}
          className="mx-auto mt-6 flex items-center gap-1.5 text-[13px] font-medium text-stone-500 transition-colors hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-100"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {c.backToSignIn}
        </button>
      ) : null}
    </div>
  );
};

export default ResetPasswordForm;